<template>
  <div class="camera-card-wrapper">
    <!-- WebRTC Stream en Video Element -->
    <div class="video-container">
      <video
        ref="videoElement"
        style="width: 100%; height: 100%; background: #000; display: block;"
        autoplay
        muted
        playsinline
      ></video>

      <!-- Canvas Overlay para dibujar QR detectado -->
      <canvas
        ref="overlayCanvas"
        style="position: absolute; top: 0; left: 0; cursor: crosshair;"
        class="overlay-canvas"
      ></canvas>

      <div v-if="!isConnected" class="connection-status">
        <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
        <p class="status-text">Conectando...</p>
      </div>
      <div v-if="connectionError" class="connection-error">
        <v-icon small color="white">error</v-icon>
        <p>{{ connectionError }}</p>
      </div>
    </div>

    <!-- Canvas para detección (invisible) -->
    <canvas ref="canvasEl" style="display: none;"></canvas>
  </div>
</template>

<script>
import jsQR from 'jsqr'

export default {
  name: 'CameraAccessCard',
  props: {
    camera: {
      type: Object,
      required: true
    },
    streamStatus: {
      type: Object,
      default: null
    },
    scanningEnabled: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      webrtcUrl: null,
      pc: null,
      isConnected: false,
      connectionError: '',
      scanTimer: null,
      lastDetectedTime: 0,
      QR_COOLDOWN: 1000,
      audioContext: null,
      processingCanvas: null,
      processingCtx: null
    }
  },
  mounted() {
    console.log('CameraAccessCard montado')
  },
  beforeDestroy() {
    this.stopQRScanner()
    if (this.pc) {
      this.pc.close()
    }
  },
  watch: {
    streamStatus(newVal) {
      if (newVal && newVal.alias) {
        this.loadWebRTCUrl()
        if (this.webrtcUrl) {
          this.initWebRTC()
        } else {
          console.error('webrtcUrl no se cargó correctamente')
        }
      }
    },
    scanningEnabled(newVal) {
      if (newVal) {
        this.startQRScanner()
      } else {
        this.stopQRScanner()
      }
    }
  },
  methods: {
    loadWebRTCUrl() {
      if (this.streamStatus && this.streamStatus.alias) {
        const serverIP = localStorage.getItem('camera_server_ip') || '127.0.0.1:7000'
        const alias = this.streamStatus.alias
        this.webrtcUrl = `http://${serverIP}/api/webrtc/${alias}`
      }
    },

    async initWebRTC() {
      try {
        this.pc = new RTCPeerConnection({
          iceServers: [
            { urls: ['stun:stun.l.google.com:19302'] },
            { urls: ['stun:stun1.l.google.com:19302'] }
          ]
        })

        this.pc.ontrack = (event) => {
          this.$refs.videoElement.srcObject = event.streams[0]
          this.isConnected = true
          this.connectionError = ''
          this.startQRScanner()
        }

        this.pc.onconnectionstatechange = () => {
          console.log('Cambio de estado de conexión:', this.pc.connectionState)
          if (this.pc.connectionState === 'failed') {
            this.isConnected = false
            this.connectionError = 'Conexión perdida'
            this.attemptReconnect()
          } else if (this.pc.connectionState === 'connected' || this.pc.connectionState === 'completed') {
            this.isConnected = true
            this.connectionError = ''
          }
        }

        // Pedir transceptor de video
        this.pc.addTransceiver('video', { recv: true })

        await this.connectWebRTC()
      } catch (err) {
        console.error('Error inicializando WebRTC:', err)
        this.connectionError = 'Error al conectar cámara'
        this.isConnected = false
      }
    },

    async connectWebRTC() {
      try {
        const offer = await this.pc.createOffer()
        await this.pc.setLocalDescription(offer)

        const response = await fetch(this.webrtcUrl, {
          method: 'POST',
          body: this.pc.localDescription.sdp,
          headers: { 'Content-Type': 'application/sdp' }
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const answer = await response.text()
        await this.pc.setRemoteDescription(
          new RTCSessionDescription({
            type: 'answer',
            sdp: answer
          })
        )
      } catch (err) {
        console.error('Error en WebRTC handshake:', err)
        this.connectionError = err.message || 'Error de conexión'
        this.isConnected = false
      }
    },

    attemptReconnect() {
      console.log('Intentando reconectar...')
      setTimeout(() => {
        if (this.pc && this.pc.connectionState === 'failed') {
          console.log('Cerrando conexión fallida e iniciando nueva...')
          this.pc.close()
          this.initWebRTC()
        }
      }, 5000)
    },

    startQRScanner() {
      if (this.scanTimer) return
      const video = this.$refs.videoElement
      const overlayCanvas = this.$refs.overlayCanvas

      // Crear canvas de procesamiento con menor resolución
      const processingCanvas = document.createElement('canvas')
      const PROCESSING_SCALE = 0.75 // Reducir resolución al 75% para mejor balance

      this.scanTimer = setInterval(() => {
        if (!video || video.readyState !== video.HAVE_ENOUGH_DATA || !video.videoWidth) {
          return
        }

        // Dimensiones de visualización
        const displayWidth = video.videoWidth
        const displayHeight = video.videoHeight

        // Dimensiones de procesamiento (más pequeñas para mejor rendimiento)
        const processWidth = Math.floor(displayWidth * PROCESSING_SCALE)
        const processHeight = Math.floor(displayHeight * PROCESSING_SCALE)

        // Actualizar dimensiones del overlay
        overlayCanvas.width = displayWidth
        overlayCanvas.height = displayHeight

        // Procesar en resolución reducida
        processingCanvas.width = processWidth
        processingCanvas.height = processHeight

        // IMPORTANTE: Obtener el contexto DESPUÉS de redimensionar
        const overlayCtx = overlayCanvas.getContext('2d')
        const processingCtx = processingCanvas.getContext('2d')

        try {
          processingCtx.drawImage(video, 0, 0, processWidth, processHeight)
        } catch (err) {
          return
        }

        const imageData = processingCtx.getImageData(0, 0, processWidth, processHeight)
        const code = jsQR(imageData.data, processWidth, processHeight)

        // Limpiar overlay antes de dibujar
        overlayCtx.fillStyle = 'transparent'
        overlayCtx.clearRect(0, 0, displayWidth, displayHeight)

        if (code && code.data && code.data.trim()) {
          const now = Date.now()
          if (now - this.lastDetectedTime > this.QR_COOLDOWN) {
            this.lastDetectedTime = now
            this.playQRSound()
            this.$emit('qr-detected', code.data)
          }

          // Escalar coordinates de vuelta a resolución original para dibujo
          const scaledLocation = {
            topLeftCorner: { x: code.location.topLeftCorner.x / PROCESSING_SCALE, y: code.location.topLeftCorner.y / PROCESSING_SCALE },
            topRightCorner: { x: code.location.topRightCorner.x / PROCESSING_SCALE, y: code.location.topRightCorner.y / PROCESSING_SCALE },
            bottomRightCorner: { x: code.location.bottomRightCorner.x / PROCESSING_SCALE, y: code.location.bottomRightCorner.y / PROCESSING_SCALE },
            bottomLeftCorner: { x: code.location.bottomLeftCorner.x / PROCESSING_SCALE, y: code.location.bottomLeftCorner.y / PROCESSING_SCALE }
          }
          this.drawQRBox(overlayCtx, scaledLocation)
        }
      }, 300) // Balance: 100ms→300ms (3.3x menos CPU, mejor detección)
    },

    drawQRBox(ctx, location) {
      const corners = [
        location.topLeftCorner,
        location.topRightCorner,
        location.bottomRightCorner,
        location.bottomLeftCorner
      ]

      // Guardar estado del contexto para restaurar después
      ctx.save()

      // Dibujar polígono con líneas (optimizado)
      ctx.strokeStyle = '#10b981'
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'

      ctx.beginPath()
      ctx.moveTo(corners[0].x, corners[0].y)
      ctx.lineTo(corners[1].x, corners[1].y)
      ctx.lineTo(corners[2].x, corners[2].y)
      ctx.lineTo(corners[3].x, corners[3].y)
      ctx.closePath()
      ctx.stroke()

      // Dibujar puntos en las esquinas (reducido a 4 arcos en lugar de múltiples operaciones)
      ctx.fillStyle = '#10b981'
      corners.forEach(corner => {
        ctx.beginPath()
        ctx.arc(corner.x, corner.y, 5, 0, 2 * Math.PI)
        ctx.fill()
      })

      // Restaurar estado
      ctx.restore()
    },

    playQRSound() {
      try {
        if (!this.audioContext) {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
        }

        const ctx = this.audioContext
        const now = ctx.currentTime
        const duration = 0.1

        // Crear oscillator para el bip
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()

        osc.connect(gain)
        gain.connect(ctx.destination)

        osc.frequency.setValueAtTime(800, now)
        osc.frequency.setValueAtTime(600, now + duration)

        gain.gain.setValueAtTime(0.3, now)
        gain.gain.exponentialRampToValueAtTime(0.01, now + duration)

        osc.start(now)
        osc.stop(now + duration)
      } catch (err) {
        console.error('Error reproduciendo sonido:', err)
      }
    },

    stopQRScanner() {
      if (this.scanTimer) {
        clearInterval(this.scanTimer)
        this.scanTimer = null
      }
    }
  }
}
</script>

<style scoped>
.camera-card-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.video-container {
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: calc(45vh - 40px);
  border-radius: 0;
  overflow: hidden;
  background: #1a1a1a;
  position: relative;
  border: 2px solid #1976d2;
}

.video-container video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.connection-status {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: rgba(0, 0, 0, 0.8);
  padding: 24px;
  border-radius: 10px;
  color: white;
  z-index: 10;
  backdrop-filter: blur(4px);
}

.status-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
}

.connection-error {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(239, 68, 68, 0.95);
  color: white;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 12px;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.connection-error p {
  margin: 0;
}
</style>
